/**
 * Admin landing for /admin/courses : two tabs (Tronc commun + Modules
 * complémentaires de base), each listing the themes with their chapter count.
 * Admin can create / rename / delete themes. Clicking a theme navigates to
 * the chapter list for that theme.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Plus,
  Trash,
  PencilSimple,
  ArrowRight,
  Folder,
  DotsSixVertical,
} from "@phosphor-icons/react";
import { coursesApi, coursesAdminApi } from "../../api/courses";
import { TRACKS } from "../../lib/tracks";
import PortalPageHeader from "../../components/portal/PortalPageHeader";
import ConfirmDialog from "../../components/courses/ConfirmDialog";
import SortableList from "../../components/courses/SortableList";

function NewThemeRow({ track, onCreated }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);

  const create = useCallback(
    async (e) => {
      e.preventDefault();
      const trimmed = title.trim();
      if (!trimmed) return;
      setSaving(true);
      try {
        await coursesAdminApi.createTheme(track, trimmed);
        setTitle("");
        onCreated?.();
      } finally {
        setSaving(false);
      }
    },
    [track, title, onCreated],
  );

  return (
    <form
      onSubmit={create}
      data-testid="admin-courses-new-theme-form"
      className="flex items-center gap-2 rounded-2xl border-2 border-dashed border-slate-200 bg-white px-4 py-3"
    >
      <Plus size={18} weight="bold" className="text-slate-400 shrink-0" />
      <input
        type="text"
        data-testid="admin-courses-new-theme-input"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={t("admin.courses.new_theme_placeholder")}
        className="flex-1 bg-transparent outline-none text-sm placeholder:text-slate-400"
      />
      <button
        type="submit"
        data-testid="admin-courses-new-theme-submit"
        disabled={saving || !title.trim()}
        className="rounded-full bg-slate-900 px-4 py-2 text-xs font-medium text-white disabled:opacity-40 hover:bg-slate-800"
      >
        {t("admin.courses.add_theme")}
      </button>
    </form>
  );
}

function ThemeRow({ theme, track, dragHandle, onChanged }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(theme.title);
  const [confirm, setConfirm] = useState(false);

  const save = useCallback(async () => {
    const trimmed = title.trim();
    if (!trimmed || trimmed === theme.title) {
      setEditing(false);
      setTitle(theme.title);
      return;
    }
    await coursesAdminApi.updateTheme(theme.id, { title: trimmed });
    setEditing(false);
    onChanged?.();
  }, [title, theme.id, theme.title, onChanged]);

  const remove = useCallback(async () => {
    await coursesAdminApi.deleteTheme(theme.id);
    setConfirm(false);
    onChanged?.();
  }, [theme.id, onChanged]);

  return (
    <div
      data-testid={`admin-theme-${theme.slug}`}
      className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-3 hover:border-slate-900 transition-colors"
    >
      <button
        type="button"
        ref={dragHandle?.setActivatorNodeRef}
        {...(dragHandle?.attributes || {})}
        {...(dragHandle?.listeners || {})}
        data-testid={`admin-theme-drag-${theme.slug}`}
        aria-label="Réorganiser"
        className="cursor-grab active:cursor-grabbing p-1 -ml-1 text-slate-400 hover:text-slate-700 touch-none"
      >
        <DotsSixVertical size={18} weight="bold" />
      </button>

      <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
        <Folder size={18} weight="regular" />
      </span>

      {editing ? (
        <input
          autoFocus
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
            if (e.key === "Escape") {
              setEditing(false);
              setTitle(theme.title);
            }
          }}
          className="flex-1 bg-transparent outline-none text-sm font-medium text-slate-900 border-b border-slate-300"
        />
      ) : (
        <Link
          to={`/admin/courses/${track}/${theme.slug}`}
          className="flex-1 text-sm font-medium text-slate-900 hover:underline"
          data-testid={`admin-theme-link-${theme.slug}`}
        >
          {theme.title}
        </Link>
      )}

      <span className="text-xs text-slate-500 hidden sm:inline">
        {theme.chapter_count} {t("admin.courses.chapters")}
      </span>

      <button
        type="button"
        onClick={() => setEditing(true)}
        aria-label="Renommer"
        className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
        data-testid={`admin-theme-rename-${theme.slug}`}
      >
        <PencilSimple size={16} />
      </button>
      <button
        type="button"
        onClick={() => setConfirm(true)}
        aria-label="Supprimer"
        className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-red-50 text-red-600"
        data-testid={`admin-theme-delete-${theme.slug}`}
      >
        <Trash size={16} />
      </button>
      <Link
        to={`/admin/courses/${track}/${theme.slug}`}
        className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
        aria-label="Ouvrir"
      >
        <ArrowRight size={16} />
      </Link>

      <ConfirmDialog
        open={confirm}
        title={t("admin.courses.delete_theme_title")}
        description={t("admin.courses.delete_theme_desc")}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        destructive
        onConfirm={remove}
        onCancel={() => setConfirm(false)}
      />
    </div>
  );
}

export default function AdminCourses() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("track") || TRACKS[0].slug;
  const [activeTrack, setActiveTrack] = useState(initialTab);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await coursesApi.tracks();
      setTracks(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const activeThemes = useMemo(
    () => tracks.find((t2) => t2.slug === activeTrack)?.themes || [],
    [tracks, activeTrack],
  );

  const setTab = useCallback(
    (slug) => {
      setActiveTrack(slug);
      setSearchParams({ track: slug });
    },
    [setSearchParams],
  );

  const onReorder = useCallback(
    async (nextThemes) => {
      // Optimistic update for the active track
      setTracks((prev) =>
        prev.map((tr) =>
          tr.slug === activeTrack ? { ...tr, themes: nextThemes } : tr,
        ),
      );
      const items = nextThemes.map((th, idx) => ({ id: th.id, order: idx + 1 }));
      try {
        await coursesAdminApi.reorderThemes(items);
      } catch {
        // On failure, reload from server
        await load();
      }
    },
    [activeTrack, load],
  );

  return (
    <div data-testid="admin-courses-page" className="space-y-10">
      <PortalPageHeader
        kickerKey="admin.placeholder.kicker"
        titleKey="admin.nav.courses"
        subtitleKey="admin.pages.courses_desc"
      />

      {/* Tabs */}
      <div
        data-testid="admin-courses-tabs"
        className="flex items-center gap-1 border-b border-slate-200"
      >
        {TRACKS.map((tr) => (
          <button
            type="button"
            key={tr.slug}
            onClick={() => setTab(tr.slug)}
            data-testid={`admin-courses-tab-${tr.slug}`}
            className={`relative px-4 py-3 text-sm font-medium transition-colors ${
              activeTrack === tr.slug
                ? "text-slate-900"
                : "text-slate-500 hover:text-slate-900"
            }`}
          >
            {t(tr.labelKey)}
            {activeTrack === tr.slug && (
              <span className="absolute inset-x-3 -bottom-px h-0.5 bg-slate-900" />
            )}
          </button>
        ))}
      </div>

      {/* Theme list */}
      <div className="space-y-3">
        {loading ? (
          <p className="text-sm text-slate-500">{t("common.loading")}</p>
        ) : activeThemes.length === 0 ? (
          <p className="text-sm text-slate-500">
            {t("admin.courses.no_theme_yet")}
          </p>
        ) : (
          <SortableList
            items={activeThemes}
            onReorder={onReorder}
            renderRow={(theme, dragHandle) => (
              <ThemeRow
                key={theme.id}
                theme={theme}
                track={activeTrack}
                dragHandle={dragHandle}
                onChanged={load}
              />
            )}
          />
        )}

        <NewThemeRow track={activeTrack} onCreated={load} />
      </div>
    </div>
  );
}
